import { Component, h, Prop, State, Watch, Listen, Element } from '@stencil/core';

import { PlaylistItem } from '../../../../interfaces';

@Component({
  tag: 'zeudy-playlist',
  styleUrl: 'zeudy-playlist.scss',
  shadow: true,
})
export class zeudyPlaylist {
  @Element() el: HTMLElement;

  /** Overlay component */
  private overlay: any;

  /** Array of videos from the Playlist */
  @Prop() items: Array<PlaylistItem>;

  /** Title for the Playlist */
  @Prop() heading: string;

  /** Count prefix string for the Playlist - Singular */
  @Prop() listTextSingular: string;

  /** Count prefix string for the Playlist - Plural */
  @Prop() listTextPlural: string;

  /** Transition duration for next video. */
  @Prop() transitionDuration: number = 5;

  /** Stores current video that is shown in the preview. */
  @State() currentPlaylistItem: PlaylistItem;

  /** Stores current video Plyr object that is shown in the preview. */
  @State() currentPlyr: any;

  /** Is a video playing. */
  @State() playing: boolean = false;

  /**
   * Listening to the Rewatch Video button click event.
   */
  @Listen('rewatch')
  rewatch() {
    this.handlePlay();
  }

  /**
   * Listening to the Next Video button click event.
   */
  @Listen('next')
  next() {
    this.handleNext();
  }

  /**
   * Plays the video, adds overlay and zeudy Video styles as soon as a new object is assigned.
   * @param value Current value
   * @param previousValue Previous value
   */
  @Watch('currentPlyr')
  handleCurrentPlay(value, previousValue) {
    if (!value) return;

    // add the overlay.
    this.addOverlay();

    // add styles from the current video config.
    this.currentPlyr.elements.container.getRootNode().host.style = this.currentPlaylistItem.config.styles;

    // if we have a previous value, then we need to autoplay the video since we are switching.
    if (previousValue !== undefined) {
      // Restore audio (YouTube starts playing on seek if the video hasn't been played yet)
      if (this.currentPlyr.provider === 'youtube' && !this.currentPlyr.muted && this.currentPlyr?.embed) {
        this.currentPlyr.embed.unMute();
      }

      this.currentPlyr.play();
    }
  }

  /**
   * Adds overlay to the player which will regulate the Next video button.
   */
  addOverlay() {
    // create overlay
    this.overlay = document.createElement('zeudy-playlist-overlay');
    // assign properties.
    this.overlay.nextItemTitle = this.getNextItemTitle();
    this.overlay.isLastItem = this.isLastItem();
    this.overlay.nextItemString = this?.listTextSingular || 'Video';
    this.overlay.transitionDuration = this.transitionDuration;
    // append
    this.currentPlyr.elements?.container?.closest('.zeudy-player__wrapper').append(this.overlay);
  }

  /**
   * Lifecycle: Component will load.
   */
  componentWillLoad() {
    // Select the first video as a current video to be shown in the playlist.
    this.currentPlaylistItem = this?.items?.[0] || null;
  }

  /**
   * Handles the click on the playlist item.
   * @param item PlaylistItem
   */
  handleItemClick(item: PlaylistItem) {
    if (this.overlay) {
      this.overlay.show = false;
    }
    this.el.style.height = this.el.offsetHeight + 'px';
    this.el.style.width = this.el.offsetWidth + 'px';
    this.currentPlaylistItem = item;
  }

  /**
   * Assign the next item in the playlist as a current item.
   */
  handleNext() {
    this.overlay.show = false;
    this.currentPlaylistItem = this.getNextItem() || this.currentPlaylistItem;
  }

  /**
   * Play the current video.
   */
  handlePlay() {
    if (this.overlay) {
      this.overlay.show = false;
    }
    this.currentPlyr.play();
  }

  /**
   * Pause the current video.
   */
  handlePause() {
    this.overlay.show = false;
    this.currentPlyr.pause();
  }

  /**
   * Get the next item in the playlist.
   * @returns PlaylistItem Next item in the playlist.
   */
  getNextItem() {
    if (this.isLastItem()) return this.items[0];

    let nextItem: PlaylistItem;
    for (let i = 0; i < this.items?.length; i++) {
      if (this.items[i]?.id === this.currentPlaylistItem?.id && this.items?.length !== i + 1) {
        nextItem = this.items[i + 1];
        break;
      }
    }
    return nextItem;
  }

  /**
   * Checks if the current item is the last item in the playlist.
   * @returns boolean True if the current item is the last item in the playlist.
   */
  isLastItem() {
    const lastIndex = this.items?.length - 1;
    return this.items[lastIndex]?.id === this.currentPlaylistItem?.id;
  }

  /**
   * Get the title of the next item in the playlist.
   * @returns string Title of the next item in the playlist.
   */
  getNextItemTitle() {
    const nextItem = this.getNextItem();
    if (undefined !== nextItem) {
      return nextItem?.title || nextItem?.config?.title;
    }
    return '';
  }

  /**
   * Rendering the component.
   * @returns Web Component
   */
  render() {
    if (!this.items?.length) return '';

    const listTextS = this.listTextSingular ? this.listTextSingular : 'Video';
    const listTextP = this.listTextPlural ? this.listTextPlural : 'Videos';

    return (
      <zeudy-playlist-ui>
        {!!this.currentPlaylistItem.config?.src ? (
          <zeudy-player
            slot="preview"
            src={this.currentPlaylistItem.config?.src}
            {...this.currentPlaylistItem.config}
            video_id={this.currentPlaylistItem.config?.id}
            id={`zeudy-player-${this.currentPlaylistItem.config?.id}`}
            media-title={this.currentPlaylistItem.config?.title}
            class={this.currentPlaylistItem.config?.playerClass}
            key={this.currentPlaylistItem.config?.id}
            provider={this.currentPlaylistItem.config?.provider}
            onPlayerReady={e => {
              this.currentPlyr = e.detail;
              this.el.style.height = null;
              this.el.style.width = null;
            }}
            onPlayedMedia={() => (this.playing = true)}
            onPausedMedia={() => (this.playing = false)}
            onEndedMedia={() => (this.overlay.show = true)}
          />
        ) : (
          <slot name="unauthorized" slot="preview" />
        )}

        <div slot="title">{this.heading || 'Playlist'}</div>

        <div slot="count">
          {this.items.length} {this.items.length > 1 ? listTextP : listTextS}
        </div>

        {this.items.map(item => {
          return (
            <zeudy-playlist-item
              slot="list"
              onClick={() => this.handleItemClick(item)}
              active={this.currentPlaylistItem?.id === item?.id}
              playing={this.currentPlaylistItem?.id === item?.id && this.playing}
              class={this.currentPlaylistItem?.id === item?.id ? 'active' : ''}
              key={item?.id}
              onTriggerPause={() => this.handlePause()}
              onTriggerPlay={() => this.handlePlay()}
            >
              <span slot="item-title">
                <span>{item?.title || item?.config?.title}</span>
              </span>
              <span slot="item-duration">
                <span>{item?.duration}</span>
              </span>
            </zeudy-playlist-item>
          );
        })}
      </zeudy-playlist-ui>
    );
  }
}
